import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import ReactDrawer from 'react-drawer';
import 'react-drawer/lib/react-drawer.css';
import deliveryFile from '../../../../../assets/files/delivery.csv';
import sprintxCourierSampleFile from '../../../../../assets/files/sprintxCourierSample.csv';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Button,
} from 'reactstrap';
import { AvForm } from 'availity-reactstrap-validation';
import { CardHeader } from 'semantic-ui-react';
import {
  fetchCheckData,
  fetchSprintCheckData,
  deliveryImportSummary,
  sprintImportSummary,
  sprintBulkAwbGenerate,
  marketplaceStockBigQueryInsert,
} from '../../action/action';

const ImportFileComponent = ({
  cardTitle = 'Upload Delivery Report',
  successMessageText = 'Delivery status updation will be reflected after some time',
  isSprintxImport = false,
  isMarketplaceStockImport = false,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null); // Use ref to clear file input

  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState(false);
  const [updateStsLoad, setUpdateDetailsLoad] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [position, setPosition] = useState();
  const [bulkLoading, setBulkLoading] = useState(false);
  const [refText, setRefText] = useState('');
  const [importType, setImportType] = useState('');
  const sampleDownloadFile = isSprintxImport
    ? sprintxCourierSampleFile
    : deliveryFile;

  const openBulkDrawer = () => {
    setPosition('right');
    setRefText('');
    setBulkOpen(true);
  };

  const closeBulkDrawer = () => {
    setBulkOpen(false);
  };

  const handleGenerateBulkAwb = async () => {
    const lsReferences = [
      ...new Set(
        refText
          .split(/[\r\n,]+/)
          .map((reference) => reference.trim())
          .filter((reference) => reference !== '')
      ),
    ];

    if (lsReferences.length === 0) {
      toast.error('Please enter at least one ls_reference');
      return;
    }

    setBulkLoading(true);
    const result = await sprintBulkAwbGenerate({ lsReferences });
    setBulkLoading(false);

    if (result.success) {
      toast.success(result.message);
      closeBulkDrawer();
    } else {
      toast.error(result.message);
    }
  };

  const handleValidSubmit = (events, values) => {
    const formData = new FormData();

    if (isMarketplaceStockImport) {
      if (!importType) {
        setErrorMsg('Please select import type');
        return;
      }

      if (selectedFile === undefined || selectedFile === null) {
        setErrorMsg('Please select the file');
        return;
      }

      const isCsvFile =
        selectedFile?.name?.toLowerCase().endsWith('.csv') ||
        selectedFile?.type === 'text/csv' ||
        selectedFile?.type === 'application/vnd.ms-excel';

      if (!isCsvFile) {
        setErrorMsg('Only CSV file is allowed');
        return;
      }

      setUpdateDetailsLoad(true);
      setErrorMsg('');
      formData.append('marketplace_file', selectedFile);
      formData.append('type', importType);

      marketplaceStockBigQueryInsert({ formData }).then((response) => {
        setUpdateDetailsLoad(false);

        const isSuccess = response?.code === 100 || response?.status === true;
        if (isSuccess) {
          toast.success(response?.message || 'File uploaded successfully', {
            style: {
              border: '1px solid #fff',
              padding: '20px',
              color: '#ffffff',
              background: '#4CAF50',
            },
            iconTheme: {
              primary: '#FFFAEE',
              secondary: '#4CAF50',
            },
          });
          setSelectedFile(null);
          setImportType('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }

        toast.error(response?.message || 'Failed to upload file', {
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#f44336',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#f44336',
          },
        });
      });
      return;
    }

    if (selectedFile === undefined || selectedFile === null) {
      setErrorMsg('Please select the file');
    } else {
      setUpdateDetailsLoad(true);
      formData.append('marketplace_file', selectedFile);

      const checkDataApi = isSprintxImport ? fetchSprintCheckData : fetchCheckData;
      checkDataApi({
        formData,
        dispatch,
      });
    }
  };

  const { deliveryCheckdata = [], isCkeckdataLoading } = useSelector(
    (state) => state.deliveryReport
  );

  useEffect(() => {
    const isCheckDataSuccess =
      deliveryCheckdata?.code === 100 || deliveryCheckdata?.status === true;
    const hasFileName = !!deliveryCheckdata?.file_name;
    const checkDataMessage =
      deliveryCheckdata?.message || deliveryCheckdata?.data?.message;

    if (isCheckDataSuccess && hasFileName) {
      setSuccessMessage(true);
      setUpdateDetailsLoad(false);
      const importSummaryApi = isSprintxImport
        ? sprintImportSummary
        : deliveryImportSummary;
      importSummaryApi({
        params: {
          file_name: deliveryCheckdata?.file_name,
          user_id: '1',
        },
        dispatch,
      });
    } else {
      if (isCheckDataSuccess && !hasFileName) {
        setUpdateDetailsLoad(false);
        toast.error('Uploaded file verification succeeded, but file_name was not received.', {
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#f44336',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#f44336',
          },
        });
      } else if (checkDataMessage) {
        setUpdateDetailsLoad(false);
        toast.error(checkDataMessage, {
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#f44336',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#f44336',
          },
        });
      }
    }
  }, [deliveryCheckdata]);

  const { importSummary = [], isImportSummaryLoading } = useSelector(
    (state) => state.deliveryReport
  );

  useEffect(() => {
    if (importSummary?.code === 100) {
      setUpdateDetailsLoad(false);
      setSelectedFile(null);

      toast.success(importSummary?.message, {
        style: {
          border: '1px solid #fff',
          padding: '20px',
          color: '#ffffff',
          background: '#4CAF50',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#4CAF50',
        },
      });
      window.location.reload(false);
    } else {
      if (importSummary?.message) {
        setUpdateDetailsLoad(false);

        toast.error(importSummary?.message, {
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#f44336',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#f44336',
          },
        });
      }
    }
  }, [importSummary]);

  // Function to clear the selected file, file input, and error message
  const clearFileSelection = () => {
    setSelectedFile(null); // Clear the selected file (content of `file`)
    setErrorMsg(''); // Clear the error message
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear the file input value
    }
  };

  return (
    <React.Fragment>
      <Row className="rowCenter">
        <Col>
          <Card>
            <CardHeader className="bg-soft-light px-3 py-3 d-flex justify-content-between">
              <CardTitle className="m-0">{cardTitle}</CardTitle>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={sampleDownloadFile}
                  download={sampleDownloadFile}
                  className="btn btn-info"
                >
                  <span className="d-flex gap-2 align-items-center">
                    <i className="bx bx-download"></i>Sample File To Download
                  </span>
                </a>
                {isMarketplaceStockImport && (
                  <span className="badge bg-soft-info text-info">
                    CSV Only
                  </span>
                )}
                {isSprintxImport && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={openBulkDrawer}
                  >
                    <i className="bx bx-printer" style={{ paddingRight: '4px' }}></i>
                    Generate Bulk AWB
                  </button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <AvForm
                onValidSubmit={(e, v) => {
                  handleValidSubmit(e, v);
                }}
              >
                <Row>
                  {isMarketplaceStockImport && (
                    <Col md={12} className="mb-3">
                      <label className="form-label">Type</label>
                      <select
                        className="form-select"
                        name="importType"
                        value={importType}
                        onChange={(e) => {
                          setImportType(e.target.value);
                          if (e.target.value) {
                            setErrorMsg('');
                          }
                        }}
                      >
                        <option value="">Select Type</option>
                        <option value="noon_not_live">Noon Not Live</option>
                        <option value="noon_live">Noon Live</option>
                        <option value="amazon_not_live">Amazon Not Live</option>
                      </select>
                    </Col>
                  )}
                  <Col md={12}>
                    <div
                      className="border-2 border-light text-center p-5 rounded position-relative"
                      style={{ borderStyle: 'dashed' }}
                    >
                      <div className="mb-3">
                        <i className="display-4 text-muted bx bx-cloud-upload"></i>
                      </div>
                      <h4>Drop files here or click to upload.</h4>
                      <input
                        type="file"
                        name="file"
                        accept={isMarketplaceStockImport ? '.csv,text/csv' : undefined}
                        ref={fileInputRef} // Attach the ref to file input
                        required
                        onChange={(e) => {
                          const file = e.target.files[0];
                          setSelectedFile(file); // Set the selected file
                          if (!file) {
                            setErrorMsg('No file selected');
                          } else {
                            setErrorMsg('');
                          }
                        }}
                        className="opacity-0 w-100 h-100 position-absolute top-0 start-0"
                      />
                    </div>
                    {/* Display the selected file name */}
                    {selectedFile && (
                      <div className="mt-3 text-start d-flex align-items-center justify-content-between gap-2 border border-success rounded bg-soft-success px-3 py-2">
                        <p className="m-0 text-success d-flex align-items-center justify-content-center gap-2">
                          <i className="mdi mdi-microsoft-excel text-success fs-24"></i>
                          <strong>Selected file:</strong> {selectedFile.name}
                        </p>
                        <i
                          className="mdi mdi-close-circle text-danger fs-24"
                          onClick={clearFileSelection} // Clear file on click
                          style={{ cursor: 'pointer' }}
                        ></i>
                      </div>
                    )}
                    {/* Error message if any */}
                    {errorMsg && (
                      <p className="text-danger mt-4 p-1 border border-danger rounded text-center">
                        {errorMsg}
                      </p>
                    )}
                  </Col>
                  <Col md={12}>
                    <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                      <Button color="dark" size="lg">
                        {updateStsLoad && (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                        SUBMIT
                      </Button>
                    </div>
                  </Col>
                </Row>
              </AvForm>
              {successMessage === true && (
                <Row>
                  <Col md={12}>
                    <p className="bg-success my-3 py-2 text-center rounded text-white bg-success fs-14">
                      <b>
                        {successMessageText}
                      </b>
                    </p>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {isSprintxImport && (
        <ReactDrawer
          open={bulkOpen}
          position={position}
          onClose={closeBulkDrawer}
          className="sideNavWidth"
        >
          <Card
            className="card-h-100 OrderDetailsBox"
            style={{ overflowY: 'scroll' }}
          >
            <CardHeader>
              <Row className="mb-3">
                <Col>
                  <CardTitle>Generate Bulk AWB</CardTitle>
                </Col>
                <Col className="textAlignRight">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkDrawer}
                    aria-label="Close"
                  ></button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <p className="text-muted font-size-13">
                Paste ls_reference values below, separated by comma or new line,
                to generate a single merged AWB PDF.
              </p>
              <Row className="mb-3">
                <Col>
                  <textarea
                    rows={8}
                    className="form-control"
                    placeholder={'WO22063069,WO22063068'}
                    value={refText}
                    onChange={(e) => setRefText(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <button
                    className="btn btn-success marginRiight"
                    onClick={handleGenerateBulkAwb}
                    disabled={bulkLoading}
                  >
                    {bulkLoading && (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                    )}
                    Generate &amp; Download
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary waves-effect waves-light"
                    onClick={closeBulkDrawer}
                    disabled={bulkLoading}
                  >
                    Cancel
                  </button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ReactDrawer>
      )}
      <Toaster position="top-right" reverseOrder={false} />
    </React.Fragment>
  );
};

export default ImportFileComponent;
